import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastProvider } from "@nimbus-ds/components";
import { connect, iAmReady, ErrorBoundary } from "@tiendanube/nexo";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import nexo from "./NexoClient";
import NexoSyncRoute from "./NexoSyncRoute";
import { SessionGate } from "./SessionGate";
import { Dashboard, FullPageState } from "./Dashboard";
import { ApprovalDetail } from "./ApprovalDetail";
import { AgentConfigure } from "./AgentConfigure";
import { AgentDetailLayout } from "./AgentDetailLayout";
import { AgentInsights } from "./AgentInsights";
import { AgentProfile } from "./AgentProfile";
import { ChatInbox } from "./ChatInbox";
import { HireLayout } from "./hire/HireLayout";
import { HireContract } from "./hire/HireContract";
import { HireAccess } from "./hire/HireAccess";
import { HireFirstDay } from "./hire/HireFirstDay";
import { HireSample } from "./hire/HireSample";
import {
  Navigate,
  Navigate as NavigateRR,
  useLocation as useLocationRR,
  useParams as useParamsRR,
} from "react-router-dom";
import { isHireBillingGateState } from "./hire/hireBillingGate";
import { initializeStoreLanguage } from "./i18n";
import { connectorLabelEs } from "./connectorLabels";
import { DashboardSkeleton } from "./Skeleton";
import PreviewModeGate from "./PreviewModeGate";
import { BillingPage } from "./BillingPage";
import { useSessionStore } from "./session";

// App shape mirrors the Storydots tiendanube-dashboard-template:
// 1. detect whether we are inside the TN admin iframe (`integrated`)
// 2. if standalone: show a minimal landing (privacy-policy / install help)
// 3. if integrated: connect → iAmReady → render router under NexoSyncRoute
//
// Without the integrated check the app tries to talk to a parent that is not
// there and hangs on "Conectando con TiendaNube" forever.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// When `VITE_DEV_ACCESS_TOKEN` is set AND we are not inside an iframe, skip
// the Nexo handshake so the app can be exercised at http://localhost:5173
// directly. The token must NOT bypass the handshake when running inside the
// real TN admin iframe — without `iAmReady(nexo)` the admin times out and
// shows its generic "Se produjo un error" page, AND the dev JWT (which is
// scoped to a local dev store) overrides the real per-store token that the
// handshake would mint. Both failure modes were observed 2026-05-11.
const DEV_TOKEN = import.meta.env.VITE_DEV_ACCESS_TOKEN as string | undefined;
const IN_IFRAME = window.location !== window.parent.location;
const USE_DEV_BYPASS = Boolean(DEV_TOKEN) && !IN_IFRAME;

// Private-beta gate: when `VITE_PREVIEW_MODE=true` is baked into the build,
// every merchant that opens the embedded app sees `PreviewModeGate` instead
// of the dashboard. The Nexo handshake + app-JWT exchange still run so the
// backend `Store` row keeps updating; only the route tree is replaced.
// Local dev keeps it off so we can drive the real dashboard against
// production data via the dev-bypass JWT.
const PREVIEW_MODE = import.meta.env.VITE_PREVIEW_MODE === "true";

export function App() {
  const [isConnected, setIsConnected] = useState(() => USE_DEV_BYPASS);
  const [hasError, setHasError] = useState(false);
  const integrated = USE_DEV_BYPASS || IN_IFRAME;
  const readySentRef = useRef(false);

  // useRef gate prevents StrictMode's mount→unmount→remount cycle from firing
  // `connect(nexo)` twice in dev. Critically, we do NOT use a cancelled-flag /
  // cleanup pattern: in StrictMode, the cleanup races the connect promise and
  // can suppress the `iAmReady(nexo)` dispatch — which causes the parent
  // admin to time out and show "Se produjo un error en la aplicación".
  // Caught 2026-05-08; the fix is to let the promise chain run unconditionally
  // (Storydots-style). If the component truly unmounts, React 18 silently
  // ignores setState on unmounted; that's acceptable.
  const connectStartedRef = useRef(false);

  useEffect(() => {
    if (!integrated || USE_DEV_BYPASS || connectStartedRef.current) return;
    connectStartedRef.current = true;
    connect(nexo)
      .then(async () => {
        // Initialize the i18n locale primitive between connect() and iAmReady()
        // — matches Storydots ordering. v1 ships a no-op (es-AR hardcoded);
        // the primitive is in place so v1.1 can plug in a real translation
        // layer without changing the boot sequence.
        await initializeStoreLanguage();
        setIsConnected(true);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("[agentes-nube] Nexo connect failed", error);
        // Reset the gate so a manual reload can retry. We do NOT auto-retry
        // because a real error (clientId mismatch, parent origin denied) will
        // recur on retry.
        connectStartedRef.current = false;
        setHasError(true);
      });
    // No cleanup function on purpose — see comment above. StrictMode unmount
    // would otherwise silently suppress the iAmReady dispatch on first mount.
  }, [integrated]);

  const handleRouteSyncReady = useCallback(() => {
    if (USE_DEV_BYPASS || readySentRef.current) return;
    readySentRef.current = true;
    // plans/08 §5.2: route-sync subscription must exist before we tell the
    // parent admin to drop its loading state. This also gates SessionGate so
    // bootstrap queries cannot race ahead of the Nexo route bridge.
    iAmReady(nexo);
  }, []);

  // OAuth return tab. The connector consent flow opens in a NEW top-level tab
  // (it has to escape the TN admin iframe — Google/Meta refuse to be framed).
  // The provider → backend callback redirects THAT tab here with
  // `?installed=<kind>` (or `?error=…`). Render a dedicated "listo, volvé al
  // panel" page that auto-closes; the embedded dashboard in the original tab
  // completes the account/property picker. Guard on `!IN_IFRAME` so we never
  // hijack the embedded surface (which no longer receives these params anyway).
  const oauthReturn = readOAuthReturn();
  if (oauthReturn && !IN_IFRAME) {
    return (
      <ToastProvider>
        <OAuthReturnPage
          installedKind={oauthReturn.installed}
          errorCode={oauthReturn.error}
        />
      </ToastProvider>
    );
  }

  // Standalone mode (e.g., direct visit to http://localhost:5173 or the
  // privacy-policy page when TN App Store reviewers access it).
  if (!integrated || hasError) {
    return (
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="*"
              element={
                <FullPageState
                  title="Abrí Agentes Nube desde el admin"
                  body="Esta app vive dentro del admin de TiendaNube. Instalala desde la App Store y abrila desde ahí."
                />
              }
            />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    );
  }

  // Integrated mode but Nexo still negotiating.
  if (!isConnected) {
    return (
      <ToastProvider>
        <FullPageState
          title="Conectando con TiendaNube"
          body="Preparando el tablero."
        />
      </ToastProvider>
    );
  }

  return (
    <ErrorBoundary nexo={nexo}>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <NexoSyncRoute
              fallback={<DashboardSkeleton />}
              onReady={handleRouteSyncReady}
            >
              <SessionGate>
              {PREVIEW_MODE ? (
                <PreviewModeGate />
              ) : (
              <Routes>
                {/* The /hire catalog was duplicated by the home roster
                    + HireConfirmModal (TuEquipo). Redirect to / so any
                    lingering deep link lands on the unified view. The
                    /hire/:slug/* sub-routes stay — the staged OAuth flow
                    still relies on them. */}
                <Route path="/hire" element={<Navigate to="/" replace />} />
                <Route path="/chats" element={<ChatInbox />} />
                {/* Facturación — embedded Appsnube Billing (separate app). Gated
                    by the BACKEND's billing_enabled (synced into the session
                    store) so the surface is absent when billing is unconfigured;
                    a stray deep link then bounces to the tablero. EXCEPTION: a
                    strict-hire gate redirect always renders it (even before the
                    flag syncs) so the hire never dead-ends — a stale flag must
                    not silently bounce the merchant to / with the gate context
                    lost (DA, 2026-06-18). */}
                <Route path="/facturacion" element={<FacturacionRoute />} />
                {/* The standalone /plan kanban moved into the Acciones tab
                    (2026-06-21). Redirect any lingering deep link — preserving
                    its query (`?agent=`, `?rec=`) — to the board there. */}
                <Route path="/plan" element={<PlanRedirect />} />
                {/* L73 P2 §3.3 — staged 3-step hire. /hire/:slug
                    redirects to /hire/:slug/contract; the layout wraps a
                    breadcrumb and the catalog fetch. */}
                <Route path="/hire/:agentSlug" element={<HireLayout />}>
                  <Route
                    index
                    element={<Navigate to="contract" replace />}
                  />
                  <Route path="contract" element={<HireContract />} />
                  <Route path="access" element={<HireAccess />} />
                  <Route path="first-day" element={<HireFirstDay />} />
                  {/* Sample-run results page (2026-06-18). The merchant fires
                      one free sample run during the interview and lands here to
                      see real results, flagged as a muestra, with a "contratá
                      para que siga corriendo" CTA. Sibling to the staged steps;
                      it reads ?installationId=<preview install>. */}
                  <Route path="sample" element={<HireSample />} />
                </Route>
                <Route
                  path="/approvals/:approvalId"
                  element={<ApprovalDetail />}
                />
                {/* L73 P2 §3.2 — agent profile root. The 4 tabs nest under
                    one parent so the shared <AgentDetailLayout/> hero
                    persists across tab switches. */}
                {/* Revamp 2026-05-11 — Conversación tab removed from the
                    per-agent detail. Per-agent chat lives in the unified
                    /chats inbox. The old `/agents/:id/chat` route now
                    redirects so old deep links land on the merged thread. */}
                <Route
                  path="/agents/:installationId"
                  element={<AgentDetailLayout />}
                >
                  <Route index element={<AgentProfile />} />
                  <Route path="insights" element={<AgentInsights />} />
                  <Route path="configure" element={<AgentConfigure />} />
                  {/* Historial tab removed (2026-06-22, #3) — its execution
                      history merged into the Perfil "Actividad reciente" feed.
                      Old /history deep-links redirect to the profile (which
                      also consumes ?run=1 to fire a run). */}
                  <Route
                    path="history"
                    element={<HistoryRedirect />}
                  />
                </Route>
                <Route
                  path="/agents/:installationId/chat"
                  element={<ChatLegacyRedirect />}
                />
                {/* Home dashboard tabs — each is a deep-linkable URL so the
                    parent TN admin can sync routes and merchants can share
                    direct links to a specific surface. */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/stats" element={<Dashboard />} />
                <Route path="/acciones" element={<Dashboard />} />
                <Route path="/analisis" element={<Dashboard />} />
                <Route path="/fuentes" element={<Dashboard />} />
                <Route path="/equipo" element={<Dashboard />} />
                <Route path="*" element={<Dashboard />} />
              </Routes>
              )}
              </SessionGate>
            </NexoSyncRoute>
          </BrowserRouter>
        </QueryClientProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

/**
 * Facturación route guard. Renders the embedded billing page when the backend
 * reports billing configured (`billing_enabled`, synced into the session store)
 * OR when a strict-hire gate routed the merchant here with context in the
 * location state. The gate-state exception closes the dead-end where backend
 * billing is configured (returns 402) but the runtime flag hasn't synced yet:
 * without it `/facturacion` would `<Navigate to="/">` and silently drop the
 * merchant on the dashboard, losing the "why you're here + volver a contratar"
 * callout (DA finding, 2026-06-18).
 */
function FacturacionRoute() {
  const location = useLocationRR();
  const billingEnabled = useSessionStore((s) => s.billingEnabled);
  if (billingEnabled || isHireBillingGateState(location.state)) {
    return <BillingPage />;
  }
  return <Navigate to="/" replace />;
}

/**
 * Old /plan deep links bounce to the Acciones tab board, preserving the query
 * string so `?agent=<installation_id>` (filter to one agent) and `?rec=<id>`
 * (highlight one card) keep working now that the kanban lives in the tab.
 */
function PlanRedirect() {
  const location = useLocationRR();
  return <NavigateRR to={`/acciones${location.search}`} replace />;
}

/** Old per-agent chat URLs land here and bounce to the unified /chats inbox. */
function ChatLegacyRedirect() {
  const params = useParamsRR();
  const id = params.installationId ?? "";
  return <NavigateRR to={id ? `/chats?agent=${id}` : "/chats"} replace />;
}

// Historial tab removed (2026-06-22, #3). Old `/agents/:id/history` links —
// including the run-trigger `?run=1` flow — redirect to the profile, which now
// hosts the run panel + the merged "Actividad reciente" feed and consumes
// `?run=1` itself. The `?run=1` query is preserved so the run still fires.
function HistoryRedirect() {
  const params = useParamsRR();
  const id = params.installationId ?? "";
  const run = new URLSearchParams(window.location.search).get("run") === "1";
  const to = id ? `/agents/${id}${run ? "?run=1" : ""}` : "/";
  return <NavigateRR to={to} replace />;
}

function readOAuthReturn(): {
  installed: string | null;
  error: string | null;
} | null {
  const params = new URLSearchParams(window.location.search);
  const installed = params.get("installed");
  const error = params.get("error");
  if (!installed && !error) return null;
  return { installed, error };
}

/**
 * OAuth-return page rendered in the NEW consent tab after the provider →
 * backend callback redirects to `…/?installed=<kind>` (or `…?error=…`). The
 * token is already persisted server-side by the time we land here. We can't run
 * the account/property picker in this tab (it's a top-level, session-less load
 * with no Nexo parent → no app JWT), so we confirm the connection, point the
 * merchant back to the TiendaNube tab where the embedded dashboard prompts the
 * picker, and auto-close. `window.close()` works because this tab was opened
 * via `window.open` in `openConnectorOAuthTab`.
 */
function OAuthReturnPage({
  installedKind,
  errorCode,
}: {
  installedKind: string | null;
  errorCode: string | null;
}) {
  const isError = Boolean(errorCode);
  const provider = installedKind
    ? connectorLabelEs(installedKind, { long: true })
    : "tu fuente";

  useEffect(() => {
    if (isError) return;
    const timer = setTimeout(() => {
      try {
        window.close();
      } catch {
        /* not script-closable (popup-blocked fallback) — copy guides instead */
      }
    }, 3500);
    return () => clearTimeout(timer);
  }, [isError]);

  return (
    <main style={RETURN_STYLES.main}>
      <section style={RETURN_STYLES.card}>
        <span
          style={{
            ...RETURN_STYLES.icon,
            ...(isError ? RETURN_STYLES.iconError : RETURN_STYLES.iconOk),
          }}
          aria-hidden="true"
        >
          {isError ? "!" : "✓"}
        </span>
        <h1 style={RETURN_STYLES.title}>
          {isError
            ? "No se completó la conexión"
            : `¡Conectaste ${provider}!`}
        </h1>
        <p style={RETURN_STYLES.body}>
          {isError
            ? "Volvé a la pestaña de TiendaNube para intentar conectar la fuente de nuevo."
            : "Ya casi está. Volvé a la pestaña de TiendaNube para elegir la cuenta y terminar de configurarla."}
        </p>
        {!isError ? (
          <p style={RETURN_STYLES.hint}>Esta pestaña se cierra sola…</p>
        ) : null}
        <button
          type="button"
          style={RETURN_STYLES.button}
          onClick={() => {
            try {
              window.close();
            } catch {
              /* ignore */
            }
          }}
        >
          Cerrar pestaña
        </button>
      </section>
    </main>
  );
}

const RETURN_STYLES: Record<string, React.CSSProperties> = {
  main: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background:
      "radial-gradient(1200px 600px at 50% -10%, #eef2ff 0%, #f7f8fb 45%, #ffffff 100%)",
    color: "#0f1729",
    fontFamily:
      '"Geist Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    WebkitFontSmoothing: "antialiased",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    textAlign: "center",
    lineHeight: 1.55,
  },
  icon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 56,
    height: 56,
    borderRadius: "50%",
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 20,
  },
  iconOk: { background: "#eefaf3", color: "#1e9e6a" },
  iconError: { background: "#fdecec", color: "#b42318" },
  title: {
    fontSize: 24,
    fontWeight: 650,
    margin: "0 0 12px",
    letterSpacing: "-0.01em",
  },
  body: {
    fontSize: 16,
    color: "#475067",
    margin: "0 auto 8px",
    maxWidth: 360,
  },
  hint: {
    fontSize: 13,
    color: "#9aa2b4",
    margin: "0 0 24px",
  },
  button: {
    appearance: "none",
    border: "1px solid #e7eaf1",
    borderRadius: 10,
    padding: "9px 18px",
    fontSize: 14,
    fontWeight: 600,
    color: "#475067",
    background: "#ffffff",
    cursor: "pointer",
  },
};

function PrivacyPolicy() {
  return (
    <FullPageState
      title="Política de privacidad"
      body="Agentes Nube procesa datos de tu tienda únicamente para que tus agentes hagan su trabajo. Más detalle en la documentación."
    />
  );
}
